import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

// Components
import MainContainer from "../../components/app/maincontainer";
import StartMapping from "../../components/modals/select-tool";
import DoneModal from "../../components/modals/donemodal";
import InfoModal from "../../components/modals/infomodal";
import { StepHeader, StepButtons } from "./components/StepNavigation";
import GeneralInformationStep from "./components/GeneralInformationStep";
import ToolCategoryStep from "./components/ToolCategoryStep";
import SelectionStep from "./components/SelectionStep";

// Stores and utilities
import { useRawdataStore } from "../../data/stores/loggerStore";
import { apiCall } from "../../data/useFetcher";
import useErrorStore from "../../data/stores/errorStore";

// Types
interface FormData {
  toolName: string;
  description: string;
  contactEmailAddress: string;
  website: string;
  webinar: string;
  logo: string;
  additionalInformation: string;
  toolCreatedBy: string;
  toolOwnerAddress: string;
  organizationName: string;
  supportingMaterialTitle: string;
  supportingMaterialFile: string;
}

interface ResourceItem {
  material?: string;
  link?: string;
  logo?: string | File;
}

interface UtilityItem {
  address?: string;
}

interface ToolSelection {
  [key: string]: string | string[];
}

interface KpiData {
  category: string;
  status: string;
  kpiId: string;
  data: any[];
  usecaseCategory: string;
}

interface StepFormProps {
  newD?: Partial<FormData>;
  newSelect?: ToolSelection;
  others?: {
    kpiSelection?: KpiData[];
    resources?: ResourceItem[];
    utilities?: UtilityItem[];
    features?: string[];
  };
}

const StepForm: React.FC<StepFormProps> = ({ newD, newSelect, others }) => {
  const navigate = useNavigate();
  const [getSearch] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [modal, setModal] = useState("");
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState<any>("");
  const [logo, setLogo] = useState<File | null>(null);
  const [supportingMaterialFile, setSupportingMaterialFile] =
    useState<File | null>(null);
  const [selection, setSelection] = useState<ToolSelection | null>(null);
  const [formInfo, setFormInfo] = useState<KpiData[]>([]);
  const [itemForm, setItemForm] = useState<ResourceItem[]>([{}]);
  const [itemUtil, setItemUtil] = useState<UtilityItem[]>([{}]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);

  const { getLogger, data, getDynamicLogger } = useRawdataStore();
  const { kpidata, regionCountry } = useRawdataStore() as any;
  const { returnErrors } = useErrorStore();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    trigger,
  } = useForm<FormData>({
    defaultValues: {
      toolName: "",
      description: "",
      contactEmailAddress: "",
      website: "",
      webinar: "",
      logo: "",
      additionalInformation: "",
      toolCreatedBy: "",
      toolOwnerAddress: "",
      organizationName: "",
      supportingMaterialTitle: "",
      supportingMaterialFile: "",
    },
  });

  // Sort tools by category
  const tools = data?.docs?.sort((a: any, b: any) =>
    a?.category?.toString()?.localeCompare(b?.category?.toString())
  );

  console.log(formInfo, "tools");

  const regionTools = regionCountry?.docs;
  const [newTool, setNewTool] = useState<any>(null);

  // Filter and combine tools
  useEffect(() => {
    if (regionTools && tools) {
      const filteredArray = tools.filter(
        (item1: any) =>
          !regionTools.some((item2: any) => item1?.category === item2?.category)
      );
      filteredArray?.splice(1, 0, ...regionTools);
      setNewTool(filteredArray);
    }
  }, [regionTools, tools]);

  // Initialize form data
  useEffect(() => {
    if (newD) reset(newD);
    if (newSelect && Object.keys(newSelect)?.length > 0) {
      setSelection((prev) => ({ ...prev, ...newSelect }));
    }
    if (others) {
      if (others?.kpiSelection) setFormInfo(others?.kpiSelection);
      if (others?.resources) setItemForm(others?.resources);
      if (others?.utilities) setItemUtil(others?.utilities);
      // Initialize features from others if available
      if (others?.features && Array.isArray(others.features)) {
        // Map feature labels back to IDs for state management
        const featureIdMap: { [key: string]: string } = {
          "Inter-system Integration": "inter-system",
          "Desktop-based, mobile or web-based": "desktop-mobile",
          "Has national and sub-national reporting linkages with other tools":
            "national-reporting",
          "Interfaces for data entry, visualization, and analysis":
            "interfaces",
          "Usable in low-connectivity areas": "low-connectivity",
          "Collects data from multiple sources (operations, surveys, etc.)":
            "multiple-sources",
          "Uses standardised indicators (like national KPIs from WSH regulator) to facilitate comparison":
            "standardised-indicators",
          "Supports continuous or scheduled data collection":
            "continuous-collection",
          "Incorporates mechanisms for data verification and error checking":
            "data-verification",
          "Can be used at various stages of the WSH service chain (planning, operations etc)":
            "service-chain",
          "Has dashboards and maps that enable quick interpretation through GIS, charts, graphs, and tables":
            "dashboards",
          "Aggregates data from local to national or regional levels":
            "data-aggregation",
          "Has analytical tools to support planning and resource allocation, and decision making":
            "analytical-tools",
          "Provides training and support for users": "training-support",
          "Cost-effective in terms of acquisition and operation for long-term use and updates":
            "cost-effective",
        };
        const featureIds = others.features
          .map((label: string) => featureIdMap[label] || label)
          .filter(Boolean);
        setSelectedFeatures(featureIds);
      }
    } else {
      // Initialize with no preselected features
      setSelectedFeatures([]);
    }
  }, [newD, reset, newSelect, others]);

  // Load initial data
  useEffect(() => {
    apiCall({
      type: "get",
      url: `/api/v1/rawdata?pagination=not`,
      getter: (d: any) => getLogger(d),
    });
    apiCall({
      type: "get",
      url: `/api/v1/tools/manage-region-country?pagination=not&useHardCoded=true`,
      getter: (d: any) => getDynamicLogger(d, "regionCountry"),
    });
  }, [getLogger, getDynamicLogger]);

  // Load KPI data when selection changes
  useEffect(() => {
    getDynamicLogger(null, "kpidata");
    if (selection?.["APPLICATION LEVEL"]) {
      apiCall({
        type: "get",
        url: `/api/v1/rawdata/manage-kpis?category=APPLICATION LEVEL&data=${selection?.["APPLICATION LEVEL"]}&pagination=not`,
        getter: (d: any) => getDynamicLogger(d, "kpidata"),
      });
    }
  }, [selection, getDynamicLogger]);

  // Update form info when KPI data changes
  useEffect(() => {
    if (selection && kpidata) {
      const thisData = [...kpidata?.docs];
      setFormInfo(thisData);
    }
  }, [kpidata, selection]);

  // Form submission handler - COMMENTED OUT FOR DEVELOPMENT
  const onSubmit = useCallback(
    async (data: FormData) => {
      setLoading(true);

      try {
        // Prepare main data in API format
        const mainData: any = {
          tools: [
            {
              // Step 1: General Information
              ...data,

              // Step 2: Tool Category Selection
              toolSelection: Object.entries(selection || {})
                .map(([key, value]) => ({
                  category: key,
                  data: Array.isArray(value) ? value : [value],
                }))
                ?.filter((it) => it?.data?.length > 0),

              // Step 3: KPI Selection
              kpiSelection: formInfo
                ?.map((it) => {
                  const filterOut = it?.data?.filter((ic) => ic?.status);
                  return { ...it, data: filterOut };
                })
                ?.filter((it) => it?.data?.length > 0)
                ?.map((it) => ({
                  category: it?.category,
                  status: it?.status,
                  kpiId: (it as any)?._id,
                  data: it?.data,
                  usecaseCategory: it?.usecaseCategory,
                })),

              // Step 3: Features Selection - Map IDs to labels
              features: selectedFeatures.map((featureId) => {
                // Feature labels mapping
                const featureLabels: { [key: string]: string } = {
                  "inter-system": "Inter-system Integration",
                  "desktop-mobile": "Desktop-based, mobile or web-based",
                  "national-reporting":
                    "Has national and sub-national reporting linkages with other tools",
                  interfaces:
                    "Interfaces for data entry, visualization, and analysis",
                  "low-connectivity": "Usable in low-connectivity areas",
                  "multiple-sources":
                    "Collects data from multiple sources (operations, surveys, etc.)",
                  "standardised-indicators":
                    "Uses standardised indicators (like national KPIs from WSH regulator) to facilitate comparison",
                  "continuous-collection":
                    "Supports continuous or scheduled data collection",
                  "data-verification":
                    "Incorporates mechanisms for data verification and error checking",
                  "service-chain":
                    "Can be used at various stages of the WSH service chain (planning, operations etc)",
                  dashboards:
                    "Has dashboards and maps that enable quick interpretation through GIS, charts, graphs, and tables",
                  "data-aggregation":
                    "Aggregates data from local to national or regional levels",
                  "analytical-tools":
                    "Has analytical tools to support planning and resource allocation, and decision making",
                  "training-support": "Provides training and support for users",
                  "cost-effective":
                    "Cost-effective in terms of acquisition and operation for long-term use and updates",
                };
                return featureLabels[featureId] || featureId;
              }),

              // Resources and Utilities
              resources: itemForm?.filter((it) => it?.link && it?.material),
              utilities: itemUtil?.filter((it) => it?.address),
            },
          ],
        };

        // Add optional fields
        if (getSearch?.get("tool")) mainData.tool = getSearch?.get("tool");

        const searchChecker =
          getSearch?.get("contactEmailAddress") && getSearch?.get("requestId");
        if (searchChecker) {
          mainData.requestId = getSearch?.get("requestId");
          mainData.contactEmailAddress = getSearch?.get("contactEmailAddress");
        }

        // Log the complete API payload
        console.log("=== COMPLETE API PAYLOAD ===");
        console.log(
          "Main Data (API Format):",
          JSON.stringify(mainData, null, 2)
        );
        console.log("Logo File:", logo);
        console.log("Supporting Material File:", supportingMaterialFile);
        console.log("Search Params:", {
          tool: getSearch?.get("tool"),
          contactEmailAddress: getSearch?.get("contactEmailAddress"),
          requestId: getSearch?.get("requestId"),
          redirect_uri: getSearch?.get("redirect_uri"),
        });

        // Handle logo upload
        if (logo) {
          const { response, errArr, errMsg } = await apiCall({
            type: "post",
            url: `/api/v1/file?returnOriginal=true`,
            data: { intendedFile: logo },
            headers: { "Content-Type": "multipart/form-data" },
            noToast: true,
          });

          if (errArr) {
            setLoading(false);
            return returnErrors(errArr);
          }
          if (errMsg) {
            setLoading(false);
            return toast.error(errMsg);
          }
          if (response) {
            const dd = response?.data?.data || response?.data || response;
            mainData.tools[0].logo = dd?.[0]?.url;
          }
        }

        // Handle supporting material file upload
        if (supportingMaterialFile) {
          const { response, errArr, errMsg } = await apiCall({
            type: "post",
            url: `/api/v1/file?returnOriginal=true`,
            data: { intendedFile: supportingMaterialFile },
            headers: { "Content-Type": "multipart/form-data" },
            noToast: true,
          });

          if (errArr) {
            setLoading(false);
            return returnErrors(errArr);
          }
          if (errMsg) {
            setLoading(false);
            return toast.error(errMsg);
          }
          if (response) {
            const dd = response?.data?.data || response?.data || response;
            mainData.tools[0].supportingMaterialFile = dd?.[0]?.url;
          }
        }

        // Handle resource uploads
        let newItemsForm = itemForm;
        if (itemForm?.length > 0) {
          const uploadedVersion = [];
          for (let u = 0; u < itemForm?.length; u++) {
            const element = itemForm?.[u];
            if (element?.material && element?.logo) {
              const { response, errArr, errMsg } = await apiCall({
                type: "post",
                url: `/api/v1/file?returnOriginal=true`,
                data: { intendedFile: element?.logo },
                headers: { "Content-Type": "multipart/form-data" },
                noToast: true,
              });

              if (errArr) {
                setLoading(false);
                return returnErrors(errArr);
              }
              if (errMsg) {
                setLoading(false);
                return toast.error(errMsg);
              }
              if (response) {
                const dd = response?.data?.data || response?.data || response;
                uploadedVersion?.push({
                  material: element?.material,
                  link: dd?.[0]?.url,
                });
              }
            }
          }
          newItemsForm = uploadedVersion;
        }

        // Update mainData with uploaded resources
        mainData.tools[0].resources = newItemsForm?.filter(
          (it) => it?.link && it?.material
        );

        // Submit the form
        const { response, errArr, errMsg } = await apiCall({
          type: "post",
          url: `/api/v1/tools/manage-${
            getSearch?.get("tool")
              ? "update"
              : searchChecker
              ? "new"
              : "anonymous"
          }-request`,
          data: mainData,
        });

        if (errArr) {
          setLoading(false);
          return returnErrors(errArr);
        }
        if (errMsg) {
          setLoading(false);
          return toast.error(errMsg);
        }

        setLoading(false);
        if (response) {
          setModal("done");
        }

        console.log("=== FORM SUBMISSION COMPLETE ===");
      } catch (error) {
        setLoading(false);
        console.error("Form submission error:", error);
      }
    },
    [
      logo,
      supportingMaterialFile,
      itemForm,
      itemUtil,
      selection,
      formInfo,
      selectedFeatures,
      getSearch,
      returnErrors,
    ]
  );

  // COMMENTED OUT ORIGINAL SUBMIT FUNCTION - UNCOMMENT WHEN READY FOR PRODUCTION
  /*
  const onSubmit = useCallback(
    async (data: FormData) => {
      setLoading(true);

      try {
        // Handle logo upload
        if (logo) {
          const { response, errArr, errMsg } = await apiCall({
            type: "post",
            url: `/api/v1/file?returnOriginal=true`,
            data: { intendedFile: logo },
            headers: { "Content-Type": "multipart/form-data" },
            noToast: true,
          });

          if (errArr) {
            setLoading(false);
            return returnErrors(errArr);
          }
          if (errMsg) {
            setLoading(false);
            return toast.error(errMsg);
          }
          if (response) {
            const dd = response?.data?.data || response?.data || response;
            data.logo = dd?.[0]?.url;
          }
        }

        // Handle resource uploads
        let newItemsForm = itemForm;
        if (itemForm?.length > 0) {
          const uploadedVersion = [];
          for (let u = 0; u < itemForm?.length; u++) {
            const element = itemForm?.[u];
            if (element?.material && element?.logo) {
              const { response, errArr, errMsg } = await apiCall({
                type: "post",
                url: `/api/v1/file?returnOriginal=true`,
                data: { intendedFile: element?.logo },
                headers: { "Content-Type": "multipart/form-data" },
                noToast: true,
              });

              if (errArr) {
                setLoading(false);
                return returnErrors(errArr);
              }
              if (errMsg) {
                setLoading(false);
                return toast.error(errMsg);
              }
              if (response) {
                const dd = response?.data?.data || response?.data || response;
                uploadedVersion?.push({
                  material: element?.material,
                  link: dd?.[0]?.url,
                });
              }
            }
          }
          newItemsForm = uploadedVersion;
        }

        // Prepare main data
        const mainData: any = {
          tools: [
            {
              ...data,
              toolSelection: Object.entries(selection || {})
                .map(([key, value]) => ({
                  category: key,
                  data: Array.isArray(value) ? value : [value],
                }))
                ?.filter((it) => it?.data?.length > 0),
              kpiSelection: formInfo
                ?.map((it) => {
                  const filterOut = it?.data?.filter((ic) => ic?.status);
                  return { ...it, data: filterOut };
                })
                ?.filter((it) => it?.data?.length > 0)
                ?.map((it) => ({
                  category: it?.category,
                  status: it?.status,
                  kpiId: (it as any)?._id,
                  data: it?.data,
                  usecaseCategory: it?.usecaseCategory,
                })),
              resources: newItemsForm?.filter((it) => it?.link && it?.material),
              utilities: itemUtil?.filter((it) => it?.address),
            },
          ],
        };

        if (getSearch?.get("tool")) mainData.tool = getSearch?.get("tool");

        const searchChecker =
          getSearch?.get("contactEmailAddress") && getSearch?.get("requestId");
        if (searchChecker) {
          mainData.requestId = getSearch?.get("requestId");
          mainData.contactEmailAddress = getSearch?.get("contactEmailAddress");
        }

        // Submit the form
        const { response, errArr, errMsg } = await apiCall({
          type: "post",
          url: `/api/v1/tools/manage-${
            getSearch?.get("tool")
              ? "update"
              : searchChecker
              ? "new"
              : "anonymous"
          }-request`,
          data: mainData,
        });

        if (errArr) {
          setLoading(false);
          return returnErrors(errArr);
        }
        if (errMsg) {
          setLoading(false);
          return toast.error(errMsg);
        }

        setLoading(false);
        if (response) {
          setModal("done");
        }
      } catch (error) {
        setLoading(false);
        console.error("Form submission error:", error);
      }
    },
    [logo, itemForm, itemUtil, selection, formInfo, getSearch, returnErrors]
  );
  */

  // KPI handler
  const handleKpi = useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement>,
      formIndex: number,
      kpiIndex: number
    ) => {
      console.log("handleKpi called:", {
        formIndex,
        kpiIndex,
        value: event?.target?.value,
      });

      setFormInfo((prev) => {
        // Prevent infinite loops by checking if the value actually changed
        const currentValue = prev[formIndex]?.data[kpiIndex]?.status;
        const newValue = event?.target?.title || event?.target?.value;

        if (currentValue === newValue) {
          console.log("Value unchanged, skipping update");
          return prev;
        }

        const newInputs = [...prev];
        const { name, value, title } = event?.target;
        const clonedObject = {
          ...newInputs[formIndex]?.data[kpiIndex],
          [name]: title || value,
          status: title || value,
        };

        const arr1 = [
          ...newInputs[formIndex]?.data?.map((it, i) =>
            i === kpiIndex ? clonedObject : it
          ),
        ];

        const result = [
          ...newInputs?.map((it, i) =>
            i === formIndex ? { ...it, data: arr1 } : it
          ),
        ];

        console.log("FormInfo updated:", result);
        return result;
      });
    },
    []
  );

  // Multiple item handlers
  const handleInputChangeForMutipleItem = (
    event: React.ChangeEvent<HTMLInputElement> | any,
    index: number,
    field: string
  ) => {
    const { value } = event?.target;
    setItemForm((prevRows) => {
      const newRows = [...prevRows];
      newRows[index][field] = value;
      return newRows;
    });
  };

  const handleInputChangeForMutipleItemUtil = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
    field: string
  ) => {
    const { value } = event.target;
    setItemUtil((prevRows) => {
      const newRows = [...prevRows];
      newRows[index][field] = value;
      return newRows;
    });
  };

  const handleDeleteRowForMutipleItem = (index: number) => {
    setItemForm((prevRows) => {
      const newRows = [...prevRows];
      newRows.splice(index, 1);
      return newRows;
    });
  };

  const handleDeleteRowForMutipleItemUtil = (index: number) => {
    setItemUtil((prevRows) => {
      const newRows = [...prevRows];
      newRows.splice(index, 1);
      return newRows;
    });
  };

  const addRowForMutipleItem = () => {
    setItemForm([...itemForm, {}]);
  };

  const addRowForMutipleItemUtils = () => {
    setItemUtil([...itemUtil, {}]);
  };

  // Step navigation handlers
  const handleNext = async () => {
    const fieldsToValidate = getFieldsForStep(currentStep);
    const isValid = await trigger(fieldsToValidate);

    if (isValid) {
      if (currentStep < 3) {
        setCurrentStep(currentStep + 1);
      } else {
        handleSubmit(onSubmit)();
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCancel = () => {
    const redirectCheck = getSearch.get("redirect_uri");
    if (redirectCheck) {
      window.open(redirectCheck);
    } else {
      navigate("/");
    }
  };

  const getFieldsForStep = (step: number): (keyof FormData)[] => {
    switch (step) {
      case 1:
        return [
          "toolName",
          "description",
          "contactEmailAddress",
          "website",
          "toolCreatedBy",
          "toolOwnerAddress",
          "logo",
          "supportingMaterialTitle",
        ];
      case 2:
        return [];
      case 3:
        return [];
      default:
        return [];
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return true; // Basic validation handled by trigger
      case 2:
        return selection && Object.keys(selection).length > 0;
      case 3:
        return formInfo && formInfo.length > 0;
      default:
        return false;
    }
  };

  const redirectCheck = getSearch.get("redirect_uri");

  return (
    <div className="bg-whit">
      <MainContainer>
        <form onSubmit={handleSubmit(onSubmit)}>
          <StepHeader
            currentStep={currentStep}
            totalSteps={3}
            onCancel={handleCancel}
          />

          {currentStep === 1 && (
            <>
              <GeneralInformationStep
                control={control}
                errors={errors}
                getSearch={getSearch}
                itemForm={itemForm}
                itemUtil={itemUtil}
                logo={logo}
                setLogo={setLogo}
                supportingMaterialFile={supportingMaterialFile}
                setSupportingMaterialFile={setSupportingMaterialFile}
                handleInputChangeForMutipleItem={handleInputChangeForMutipleItem}
                handleInputChangeForMutipleItemUtil={
                  handleInputChangeForMutipleItemUtil
                }
                handleDeleteRowForMutipleItem={handleDeleteRowForMutipleItem}
                handleDeleteRowForMutipleItemUtil={
                  handleDeleteRowForMutipleItemUtil
                }
                addRowForMutipleItem={addRowForMutipleItem}
                addRowForMutipleItemUtils={addRowForMutipleItemUtils}
              />
              <StepButtons
                currentStep={currentStep}
                totalSteps={3}
                onPrevious={handlePrevious}
                onNext={handleNext}
                canProceed={canProceed()}
                isLoading={loading}
                onCancel={handleCancel}
              />
            </>
          )}

          {currentStep === 2 && (
            <>
              <ToolCategoryStep
                tools={tools || []}
                selection={selection}
                formInfo={formInfo}
                handleKpi={handleKpi}
                setModal={setModal}
                setInfo={setInfo}
                others={others}
              />
              <StepButtons
                currentStep={currentStep}
                totalSteps={3}
                onPrevious={handlePrevious}
                onNext={handleNext}
                canProceed={canProceed()}
                isLoading={loading}
                onCancel={handleCancel}
              />
            </>
          )}

          {currentStep === 3 && (
            <>
              <SelectionStep
                formInfo={formInfo}
                handleKpi={handleKpi}
                setInfo={setInfo}
                others={others}
                selectedFeatures={selectedFeatures}
                setSelectedFeatures={setSelectedFeatures}
              />
              <StepButtons
                currentStep={currentStep}
                totalSteps={3}
                onPrevious={handlePrevious}
                onNext={handleNext}
                canProceed={canProceed()}
                isLoading={loading}
                onCancel={handleCancel}
              />
            </>
          )}
        </form>

        {modal === "start" && (
          <StartMapping
            handleClose={() => setModal("")}
            data={newTool}
            handleComplete={(da) => {
              setSelection(da);
              setModal("");
            }}
            defaultSelection={selection}
          />
        )}

        {modal === "done" && (
          <DoneModal
            page="Tool"
            mainText="Tool Request Submitted"
            handleClose={() => setModal("")}
            path="/"
            redirect={redirectCheck}
            text={redirectCheck ? "Continue" : "Go to Map"}
            subtext={
              getSearch?.get("contactEmailAddress") &&
              getSearch?.get("requestId")
                ? ""
                : "You will be notified within 5 working days pending review by the admin"
            }
          />
        )}

        {info && (
          <InfoModal
            handleClose={() => setInfo("")}
            title={info?.category || "Infrastructure and Stability"}
            description={info?.description}
          />
        )}
      </MainContainer>
    </div>
  );
};

export default StepForm;
